<template>
    <div>
        <div>
            <div class="form-actions flex justify-between">
                <div class="flex vx-col-action">
                    <vs-input icon-pack="feather" icon="icon-search" placeholder="Type here..." v-model="globalSearchTerm" class="is-label-placeholder searchBar-input" id="filter-text-box" v-on:input="onFilterTextBoxChanged()" />
                    <span class="br-line mr-2 ml-2"></span>
                </div>

                <div class="float-right flex">
                    <vs-chip class="chip-selected-rows" transparent closable color="primary" @click="deselectAllSelectedRows()">
                        {{ this.gridApi ? this.gridApi.getSelectedNodes().length : 0 }} Rows Selected
                    </vs-chip>
                    <vx-tooltip :text="
					!flags['selectedAll'][this.currentPage - 1]
                    ? 'Select All'
                    : 'Deselect All'"
                    position="top">
                        <vs-button class="iconOnly" color="dark" type="flat" icon-pack="feather" :icon="
						flags['selectedAll'][this.currentPage - 1]
						? 'icon-check-square'
						: 'icon-square'"
                        @click="toggleSelectAll()"></vs-button>
                    </vx-tooltip>

                    <span class="br-line-long mr-2 ml-2 h-60"></span>

                    <vx-tooltip v-if="isLocalRule" text="Add MIS" position="top">
                        <vs-button class="iconOnly" color="dark" type="flat" icon-pack="feather" icon="icon-plus" @click="addPromo()"></vs-button>
                    </vx-tooltip>

                    <vx-tooltip v-if="isLocalEditRule" text="Edit MIS" position="top">
                        <vs-button class="iconOnly" color="dark" type="flat" icon-pack="feather" icon="icon-edit" @click="editPromo()" :disabled="this.selectedRowCount == 0"></vs-button>
                    </vx-tooltip>

                    <vx-tooltip v-if="isLocalRule" text="Duplicate MIS" position="top">
                        <vs-button class="iconOnly" color="dark" type="flat" icon-pack="feather" icon="icon-copy" @click="duplicatePromo()" :disabled="this.selectedRowCount !== 1"></vs-button>
                    </vx-tooltip>

                    <span class="br-line-long mr-2 ml-2 h-60"></span>

                    <vx-tooltip text="Download list" position="top">
                        <vs-button class="iconOnly" color="dark" type="flat" icon-pack="feather" icon="icon-download" @click="downloadFilteredCsv()"></vs-button>
                    </vx-tooltip>

                    <vx-tooltip text="Reset Filters" position="top">
                        <vs-button class="iconOnly" color="dark" type="flat" icon-pack="feather" icon="icon-refresh-ccw" @click="refreshPageWithFilters()"></vs-button>
                    </vx-tooltip>
                </div>
            </div>

            <div id="grid-loader" class="vx-card vs-con-loading__container">
                <ag-grid-vue :gridOptions="gridOptions" class="ag-theme-material w-100 mb-4 ag-grid-table" :columnDefs="columnDefs" :defaultColDef="defaultColDef" :components="components" :rowData="tableData" rowSelection="multiple" :enableRangeSelection="true" :suppressCopyRowsToClipboard="true" :suppressDragLeaveHidesColumns="true" :animateRows="false" :floatingFilter="true" :pagination="false" :suppressPaginationPanel="true" :suppressRowClickSelection="true" :rowClassRules="rowClassRules" :statusBar="statusBar" :sideBar="sideBar" :rowHeight="30" :headerHeight="30" :floatingFiltersHeight="35" :checkboxSelection="true" :enableBrowserTooltips="true" @filter-changed="onFilterChanged" @sort-changed="onSortChanged" @row-selected="onRowSelected" @column-resized="onColumnResized">
                </ag-grid-vue>
                <div class="vx-row py-5">
                    <div class="vx-col w-full">
                        <vs-pagination :total="totalPages" :max="maxPageNumbers" class="custom-pagination" v-model="currentPage" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "ag-grid-enterprise";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import "@/assets/scss/radix/extraComponents/agGridStyleOverride.scss";

import CellRendererStatus from "@/components/ag-grid-tables/actions/CellRendererStatus.vue";
import EditMISPromo from "@/components/ag-grid-tables/promos/EditMISPromo.vue";

// Maps ag-grid column ids (floating filters) to the flat query params the
// backend's MisRepository::applyMisFilters understands.
// Client Name / Registrar Name / AM / TLD: field stays name-based (cell
// rendering untouched), Set Filter checkbox options are populated from the
// pre-fetched options list, and selected names are resolved to id(s) in
// buildFilterParams() ("set-id") right before the request goes out.
// sortKey is only needed where the backend's sort_by key differs from the
// filter param above (e.g. filtering resellers/registrars is by id, sorting
// is by the joined name) — omitted entries fall back to `param` below.
const FILTER_PARAM_MAP = {
    "new_reseller.name": { param: "reseller_id", type: "set-id", optionsKey: "resellerOptions", nameKey: "name", sortKey: "reseller_name" },
    "new_reseller.unique_registrar.name": { param: "registrar_id", type: "set-id", optionsKey: "registrarOptions", nameKey: "name", sortKey: "registrar_name" },
    "am.name": { param: "am_id", type: "set-id", optionsKey: "amOptions", nameKey: "name", sortKey: "am_name" },
    "tld.tld_name": { param: "tld_id", type: "set-id", optionsKey: "tldOptions", nameKey: "tld_name", sortKey: "tld" },
    "status": { param: "status", type: "set" },
    "type": { param: "type", type: "set" },
    "start_date": { param: "start_date", type: "date" },
    "end_date": { param: "end_date", type: "date" },
    "period": { param: "period", type: "set" },
    "list_based": { param: "list_based", type: "set", valuesMap: { "Yes": "yes", "No": "no" } },
};

// type/status are queried live (MIS has values the other promo tracks
// don't, e.g. "normal expiry" / "updated"); period/list_based are fixed
// enums the backend hardcodes — see MisController::getFilterMeta. Shared
// across every mounted instance of this component (active + expired tabs
// both render one) so it's fetched at most once per session.
let filterMetaCache = null;

export default {
    name: "MISPromoListComponent",
    components: {
        AgGridVue,
        CellRendererStatus,
        EditMISPromo,
    },
    props: {
        expired: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedRowCount: "",
            rejectedPromo: false,
            activePrompt: false,
            reject_comments: "",
            autoGroupColumnDef: null,
            gridOptions: {},
            maxPageNumbers: 7,
            gridApi: null,
            currentPage: 1,
            perPage: 18,
            totalRows: 0,
            totalPagesFromApi: 0,
            searchDebounceTimer: null,
            suppressGridEvents: false,
            // Name->id lookup source for reseller/registrar/am/tld filters,
            // fetched once on load — never refetched when promo row data
            // refreshes/paginates. Used only to translate what the user
            // checks in the (unchanged) name-based filter boxes into ids
            // before the request goes out; row/cell data is untouched.
            resellerOptions: [],
            registrarOptions: [],
            tldOptions: [],
            amOptions: [],
            typeOptions: [],
            statusOptions: [],
            periodOptions: [],
            listBasedOptions: [],
            defaultColDef: {
                sortable: true,
                resizable: true,
                suppressMenu: true,
                suppressMovable: false,
                filterParams: {
                    suppressAndOrCondition: true,
                    newRowsAction: "keep",
                },
            },
            rowClassRules: null,
            statusBar: null,
            sideBar: null,
            columnDefs: null,
            tableData: null,
            selectedPromos: [],
            flags: {
                selectedAll: [],
            },
            // Cell Renderer Components
            components: {
                CellRendererStatus,
            },
            globalSearchTerm: "",
        };
    },
    watch: {
        windowWidth(val) {
            // Toggle colummn movable
            this.toggleColumnMovable(val);
            // Toggle colummn pinning
            this.toggleColumnPinned(val);
        },
        currentPage(val, oldVal) {
            if (val !== oldVal) {
                this.populateTableData();
            }
        },
    },
    computed: {
        // Separate persistence per tab instance ("MIS" vs "Expired > MIS"),
        // since Lists.vue mounts two independent instances of this component.
        storageKey() {
            return this.expired
                ? "misPromoListFilters_expired"
                : "misPromoListFilters";
        },
        windowWidth: function () {
            return this.$store.state.windowWidth;
        },
        // user_role() {
        //     return JSON.parse(localStorage.getItem("userDetails")).role;
        // },
        user_id() {
            return JSON.parse(localStorage.getItem("userDetails")).id;
        },
        status() {
            // console.log(data && data ? data.status:'');
            return event.node;
        },
        paginationPageSize() {
            return this.perPage;
        },
        totalPages() {
            if (this.totalPagesFromApi) {
                return this.totalPagesFromApi;
            }

            return this.totalRows ? Math.ceil(this.totalRows / this.perPage) : 0;
        },
        validComment() {
            return this.reject_comments.length > 0;
        },
        rejectTitle() {
            return "Reject Selected Promos?";
        },
        yesText() {
            return "Yes";
        },
        noText() {
            return "No";
        },
        isLocalRule() {
            return this.$acl.check('isSuperAdmin') || this.$acl.check('isChannelAdmin') || this.$acl.check('isBIAdmin') || this.$acl.check('isChannelUser');
        },
        isLocalEditRule() {
            return this.$acl.check('isSuperAdmin') || this.$acl.check('isChannelAdmin') || this.$acl.check('isBIAdmin') || this.$acl.check('isChannelUser') || this.$acl.check('isBIUser');
        },
    },
    methods: {
        onPageSizeChanged(newPageSize) {
            this.perPage = Number(newPageSize) || 15;
            if (this.currentPage === 1) {
                this.populateTableData();
            } else {
                this.currentPage = 1;
            }
        },
        onRowSelected(event) {
            var getStatus = this.gridApi
                .getSelectedNodes()
                .map((x) => x.data)
                .map((x) => x.status);
            getStatus.includes("rejected")
                ? (this.rejectedPromo = true)
                : (this.rejectedPromo = false);
            if (event.node.selected) {
                // Push to selectedPromos
                // console.log('lenght', this.gridApi.getSelectedNodes().length);
                this.selectedRowCount = this.gridApi.getSelectedNodes().length;
                if (this.selectedRowCount > 30) {
                    event.node.setSelected(false);

                    this.$vs.notify({
                        time: 5000,
                        color: "warning",
                        title: "Warning",
                        text: "You may select only upto 30 rows",
                    });
                }
            } else {
                // Remove from selectedPromos
                this.selectedRowCount = this.gridApi.getSelectedNodes().length;
            }
        },
        onFilterChanged() {
            if (this.suppressGridEvents) {
                return;
            }

            this.deselectAllSelectedRows();
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                this.persistFilterState();
                if (this.currentPage === 1) {
                    this.populateTableData();
                } else {
                    this.currentPage = 1;
                }
            }, 350);
        },
        onSortChanged() {
            if (this.suppressGridEvents) {
                return;
            }

            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                this.persistFilterState();
                if (this.currentPage === 1) {
                    this.populateTableData();
                } else {
                    this.currentPage = 1;
                }
            }, 350);
        },
        // NOTE: If logged in user is Erica, default the AM filter to her own
        // name, don't apply a default filter for any other logged in user.
        applyAmExclusionFilter() {
            if (!this.gridApi || !this.isAMExclusionActive()) {
                return;
            }

            const exclusionName = (process.env.VUE_APP_AM_EXCLUSION || "").trim();
            if (!exclusionName) {
                return;
            }

            const currentFilterModel = this.gridApi.getFilterModel() || {};
            const currentAMFilter = currentFilterModel["am.name"];
            const alreadyApplied = currentAMFilter &&
                Array.isArray(currentAMFilter.values) &&
                currentAMFilter.values.length === 1 &&
                currentAMFilter.values[0] === exclusionName;

            if (alreadyApplied) {
                return;
            }

            this.gridApi.setFilterModel({
                'am.name': {
                    type: 'set',
                    values: [exclusionName],
                },
            });
        },
        // tlds/ams are already fetched once at login and kept in the store,
        // so read them straight from there instead of re-requesting. Only
        // resellerOptions/registrarOptions (which resellers/registrars
        // actually have MIS records) need their own request.
        // Fetched once when the component mounts; never refetched on data
        // refresh/pagination, so the checkbox lists stay stable while the
        // promos section reloads underneath them.
        // tlds/ams/registrars/resellers are all already fetched once at
        // login and kept in the store — a reseller is a reseller regardless
        // of which promo page asks, so just read them straight from there
        // instead of every list page re-requesting its own copy.
        fetchFilterOptions() {
            this.tldOptions = this.$store.state.tlds || [];
            this.amOptions = this.$store.state.ams || [];
            this.registrarOptions = this.$store.state.registrars || [];
            this.resellerOptions = this.$store.state.resellers || [];

            if (filterMetaCache) {
                this.typeOptions = filterMetaCache.types;
                this.statusOptions = filterMetaCache.statuses;
                this.periodOptions = filterMetaCache.periods;
                this.listBasedOptions = filterMetaCache.listBasedOptions;
                return Promise.resolve();
            }

            return this.$axiosSecure
                .get("/mis-records/filter-meta")
                .then((response) => {
                    filterMetaCache = {
                        types: (response.data && response.data.types) || [],
                        statuses: (response.data && response.data.statuses) || [],
                        periods: (response.data && response.data.periods) || [],
                        listBasedOptions: (response.data && response.data.listBasedOptions) || [],
                    };
                    this.typeOptions = filterMetaCache.types;
                    this.statusOptions = filterMetaCache.statuses;
                    this.periodOptions = filterMetaCache.periods;
                    this.listBasedOptions = filterMetaCache.listBasedOptions;
                })
                .catch((error) => console.log(error));
        },
        // Persist search/filter/sort state so it survives this component being
        // unmounted and remounted (e.g. switching away from this tab and back).
        persistFilterState() {
            let state = {
                globalSearchTerm: this.globalSearchTerm,
                filterModel: this.gridApi ? (this.gridApi.getFilterModel() || {}) : {},
                sortModel: this.gridApi ? (this.gridApi.getSortModel() || []) : [],
            };

            try {
                sessionStorage.setItem(this.storageKey, JSON.stringify(state));
            } catch (e) {
                // Ignore storage errors (e.g. private browsing quota exceeded).
            }
        },
        restorePersistedState() {
            let saved = null;

            try {
                saved = JSON.parse(sessionStorage.getItem(this.storageKey));
            } catch (e) {
                saved = null;
            }

            if (!saved) {
                return;
            }

            if (saved.globalSearchTerm) {
                this.globalSearchTerm = saved.globalSearchTerm;
            }

            let hasFilterModel = saved.filterModel && Object.keys(saved.filterModel).length;
            let hasSortModel = Array.isArray(saved.sortModel) && saved.sortModel.length;

            if (this.gridApi && (hasFilterModel || hasSortModel)) {
                this.suppressGridEvents = true;

                if (hasFilterModel) {
                    this.gridApi.setFilterModel(saved.filterModel);
                }

                if (hasSortModel) {
                    this.gridApi.setSortModel(saved.sortModel);
                }

                this.$nextTick(() => {
                    this.suppressGridEvents = false;
                });
            }
        },
        // Builds the search/filter/sort query params shared by the paginated
        // list request and the download/export request, so both always stay
        // in sync with whatever's currently applied in the grid.
        buildFilterParams() {
            let params = {};

            if (this.globalSearchTerm && this.globalSearchTerm.trim()) {
                params.search = this.globalSearchTerm.trim();
            }

            if (this.gridApi) {
                let sortModel = this.gridApi.getSortModel() || [];
                let filterModel = this.gridApi.getFilterModel() || {};

                if (sortModel.length) {
                    let sortColId = sortModel[0].colId;
                    let sortMapping = FILTER_PARAM_MAP[sortColId];
                    params.sort_by = sortMapping ? (sortMapping.sortKey || sortMapping.param) : sortColId;
                    params.sort_dir = sortModel[0].sort;
                }

                Object.keys(filterModel).forEach((colId) => {
                    let mapping = FILTER_PARAM_MAP[colId];
                    if (!mapping) {
                        return;
                    }

                    let model = filterModel[colId];
                    let value = null;

                    if (mapping.type === "text") {
                        value = model && model.filter;
                    } else if (mapping.type === "set-id") {
                        // Checkbox selections here are names (matching the
                        // column's own field) — resolve them to id(s) right
                        // before the request goes out, using the options
                        // list already fetched once on load.
                        let selectedNames = model && Array.isArray(model.values) ? model.values : [];
                        if (selectedNames.length) {
                            let ids = (this[mapping.optionsKey] || [])
                                .filter((o) => selectedNames.includes(String(o[mapping.nameKey])))
                                .map((o) => o.id);
                            value = ids.length ? ids.join(",") : null;
                        }
                    } else if (mapping.type === "set") {
                        let selectedValues = model && Array.isArray(model.values) ? model.values : [];
                        // Some Set Filters show a formatted/derived value (e.g.
                        // list_based's Yes/No) rather than the raw column value —
                        // valuesMap translates back before it's sent to the API.
                        if (mapping.valuesMap) {
                            selectedValues = selectedValues.map((v) =>
                                Object.prototype.hasOwnProperty.call(mapping.valuesMap, v) ? mapping.valuesMap[v] : v
                            );
                        }
                        value = selectedValues.length ? selectedValues.join(",") : null;
                    } else if (mapping.type === "date") {
                        value = model && model.dateFrom ? model.dateFrom.split(" ")[0] : null;
                    }

                    if (value) {
                        params[mapping.param] = value;
                    }
                });
            }

            return params;
        },
        openLoadingInDiv() {
            this.$vs.loading({
                container: "#div-with-loading",
                scale: 0.8,
            });
        },
        hideLoadingInDiv() {
            this.$vs.loading.close("#div-with-loading > .con-vs-loading");
        },
        scrollHanle(evt) {
            // console.log(evt)
        },
        toggleColumnMovable(val) {
            if (val <= 576) {
                this.defaultColDef["suppressMovable"] = true;
            } else {
                this.defaultColDef["suppressMovable"] = false;
            }
        },
        toggleColumnPinned(val) {
            if (val <= 576) {
                this.maxPageNumbers = 4;
                this.gridOptions.columnApi.setColumnPinned("domain", null);
            } else {
                this.gridOptions.columnApi.setColumnPinned("domain", "left");
            }
        },
        onColumnResized() {
            this.gridApi.resetRowHeights();
        },
        sizeColumnsToFit() {
            this.gridApi.sizeColumnsToFit();
        },
        disableQueue() {
            this.rowClassRules = {
                "ag-row-disabled": 'data && (data.status == "edit pending")',
                "ag-checkbox-disabled":
                    'data && (data.status == "edit pending")',
            };
        },
        // Refresh the page without resetting filters
        refreshPage() {
            this.flags.selectedAll = Array(this.totalPages || 1).fill(false);
            // Reset selection
            this.gridApi.deselectAll();
            // Reset sort
            this.gridApi.setSortModel(null);
            // Table Data AXIOS Call
            this.populateTableData();
        },
        refreshPageWithFilters() {
            this.flags.selectedAll = Array(this.totalPages || 1).fill(false);
            this.globalSearchTerm = "";

            try {
                sessionStorage.removeItem(this.storageKey);
            } catch (e) {
                // Ignore storage errors.
            }

            // Reset filters
            this.gridApi.setFilterModel(null);
            // Reset selection
            this.gridApi.deselectAll();
            // Reset sort
            this.gridApi.setSortModel(null);
            // Table Data AXIOS Call
            if (this.currentPage === 1) {
                this.populateTableData();
            } else {
                this.currentPage = 1;
            }
        },
        populateTableData() {
            let self = this;

            let url = this.expired
                ? "/mis-records/expired"
                : "/mis-records";

            let params = Object.assign(
                {
                    page: this.currentPage,
                    limit: this.perPage,
                },
                this.buildFilterParams()
            );

            this.$axiosSecure
                .get(url, { params })
                .then((response) => {
                    let responseData = response.data;

                    // Backward compatibility with old array-only response shape.
                    if (Array.isArray(responseData)) {
                        this.tableData = responseData;
                        this.totalRows = responseData.length;
                        this.totalPagesFromApi = 0;
                    } else {
                        this.tableData = responseData.data || [];
                        this.totalRows = Number(
                            responseData.total ||
                            (responseData.meta && responseData.meta.total) ||
                            this.tableData.length
                        );

                        this.totalPagesFromApi = Number(responseData.last_page || 0);

                        if (responseData.per_page) {
                            this.perPage = Number(responseData.per_page);
                        }
                    }

                    this.flags.selectedAll = Array(this.totalPages || 1).fill(false);

                    if (this.gridApi) {
                        setTimeout(() => {
                            this.applyAmExclusionFilter();
                        }, 50);
                    }
                })
                .catch((error) => {
                    // Error notification
                    self.$vs.notify({
                        color: "danger",
                        title: "Something went wrong",
                        text: "Please contact the server admin",
                    });

                    console.log(error);

                    // Clear Listing Count
                    //this.$store.dispatch("clearListingCount");
                });
        },
        gridSelectAll() {
            this.gridApi.forEachNodeAfterFilter((node) => {
                if (
                    !(
                        node.data.status == "new approved" ||
                        node.data.status == "rejected" ||
                        node.data.status == "error"
                    )
                ) {
                    node.setSelected(true);
                }
            });
        },
        gridDeselectAll() {
            this.gridApi.forEachNodeAfterFilter((node) => {
                node.setSelected(false);
            });
        },
        addPromo() {
            this.$router.push("/promos/add/cnic");
        },
        editPromo() {
            let extended = false;

            if (this.expired) {
                extended = true;
            }

            var selectedRowsData = this.gridApi
                .getSelectedNodes()
                .map((x) => x.data);
            this.$root.$emit(
                "selectedGroupEditMIS",
                selectedRowsData,
                extended
            );
        },
        duplicatePromo() {
            // console.log('selected', this.gridApi.getSelectedNodes());
            // console.log("selected data", this.gridApi.getSelectedNodes());
            var selectedRowsData = this.gridApi
                .getSelectedNodes()
                .map((x) => x.data);
            // this.$root.$emit('selectedDuplicate', selectedRowsData);
            this.$root.$emit("duplicatePromoMIS", selectedRowsData);
        },
        deletePromo() {
            this.openLoadingInDiv();

            // console.log('selected', this.gridApi.getSelectedNodes());
            var selectedRowData = this.gridApi
                .getSelectedNodes()
                .map((x) => x.data);
            var selectedRowId = selectedRowData.map((x) => x.id);

            // console.log("selectedRowId", selectedRowId);
            // return;

            this.$axiosSecure
                .post("/local-promos/delete", {
                    id: selectedRowId,
                })

                .then((response) => {
                    //console.log("success");
                    setTimeout(() => {
                        this.refreshPage();
                        //Hide loader
                        this.hideLoadingInDiv();
                        // Confimation notification
                        this.$vs.dialog({
                            color: "success",
                            title: "Deleted successfully",
                            text: "Selcted Internal Promos have been deleted.",
                            acceptText: "Close",
                            accept: () => {},
                        });
                    }, 100);
                })
                .catch((error) => {
                    console.log("error", error);
                    this.hideLoadingInDiv();
                    //Error msg
                    this.$vs.dialog({
                        color: "danger",
                        title: "Something went wrong",
                        text: "Please contact the server admin",
                        acceptText: "Close",
                        accept: () => {},
                    });
                });
        },
        // Downloads the full filtered dataset from the backend (not just the
        // currently loaded page), applying whatever search/filter/sort is
        // currently active in the grid.
        downloadFilteredCsv() {
            let params = this.buildFilterParams();

            this.$vs.loading();

            this.$axiosSecure
                .get("/mis-records/download", { params, responseType: "blob" })
                .then((response) => {
                    let blob = new Blob([response.data], { type: "text/csv" });
                    let link = document.createElement("a");
                    link.href = URL.createObjectURL(blob);
                    link.download = "mis-records.csv";
                    link.click();
                    URL.revokeObjectURL(link.href);
                })
                .catch((error) => {
                    this.$vs.notify({
                        color: "danger",
                        title: "Something went wrong",
                        text: "Please contact the server admin",
                    });
                    console.log(error);
                })
                .finally(() => {
                    this.$vs.loading.close();
                });
        },
        toggleSelectAll() {
            if (this.flags["selectedAll"][this.currentPage - 1]) {
                this.gridDeselectAll();
                this.flags["selectedAll"][this.currentPage - 1] = !this.flags["selectedAll"][this.currentPage - 1];
            } else {
                this.selectedRowCount = this.gridApi.getSelectedNodes().length;
                if (this.selectedRowCount < 30 && this.selectedRowCount + this.perPage <= 30) {
                    this.gridSelectAll();
                    this.flags["selectedAll"][this.currentPage - 1] =
                        !this.flags["selectedAll"][this.currentPage - 1];
                } else {
                    this.$vs.notify({
                        time: 5000,
                        color: "warning",
                        title: "Warning",
                        text: "You may select only upto 30 rows",
                    });
                }
            }
        },
        onFilterTextBoxChanged() {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                this.persistFilterState();
                this.deselectAllSelectedRows();
                if (this.currentPage === 1) {
                    this.populateTableData();
                } else {
                    this.currentPage = 1;
                }
            }, 350);
        },
        initSelectedAllFlag() {
            this.flags.selectedAll = Array(this.totalPages || 1).fill(false);
        },
        deselectAllSelectedRows() {
            this.gridApi.deselectAll();

            this.flags["selectedAll"].fill(false);
        },
    },
    beforeMount() {
        this.disableQueue();

        // Reset movable columns on mobile
        this.toggleColumnMovable(this.windowWidth);

        (this.columnDefs = [
            {
                headerName: "Client Name",
                field: "new_reseller.name",
                filter: "agSetColumnFilter",
                filterParams: {
                    suppressAndOrCondition: true,
                    newRowsAction: "keep",
                    values: (params) => {
                        params.success(this.resellerOptions.map((r) => r.name));
                    },
                },
                minWidth: 200,
                checkboxSelection: this.isLocalEditRule,
                pinned: "left",
                tooltipField: "new_reseller.name",
                // cellClass: "cellCustomWrap",
            },
            {
                headerName: "Type",
                field: "type",
                filter: "agSetColumnFilter",
                filterParams: {
                    suppressAndOrCondition: true,
                    newRowsAction: "keep",
                    values: (params) => {
                        params.success(this.typeOptions);
                    },
                },
                minWidth: 170,
                pinned: "left",
            },
            {
                headerName: "TLD",
                field: "tld.tld_name",
                filter: "agSetColumnFilter",
                filterParams: {
                    suppressAndOrCondition: true,
                    newRowsAction: "keep",
                    values: (params) => {
                        params.success(this.tldOptions.map((t) => t.tld_name));
                    },
                },
                minWidth: 100,
                width: 150,
                pinned: "left",
            },
            {
                headerName: "Registrar Name",
                field: "new_reseller.unique_registrar.name",
                filter: "agSetColumnFilter",
                filterParams: {
                    suppressAndOrCondition: true,
                    newRowsAction: "keep",
                    values: (params) => {
                        params.success(this.registrarOptions.map((r) => r.name));
                    },
                },
                minWidth: 175,
                tooltipField: "new_reseller.unique_registrar.name",
                // cellClass: "cellCustomWrap",
            },
            {
                headerName: "Status",
                field: "status",
                filter: "agSetColumnFilter",
                filterParams: {
                    suppressAndOrCondition: true,
                    newRowsAction: "keep",
                    values: (params) => {
                        params.success(this.statusOptions);
                    },
                },
                cellRendererFramework: "CellRendererStatus",
                minWidth: 100,
            },
            {
                headerName: "AM",
                field: "am.name",
                filter: "agSetColumnFilter",
                filterParams: {
                    suppressAndOrCondition: true,
                    newRowsAction: "keep",
                    values: (params) => {
                        params.success(this.amOptions.map((a) => a.name));
                    },
                },
                minWidth: 175,
                width: 175,
                suppressFilter: this.isAMExclusionActive() ? true : false
            },
            {
                headerName: "Effective 1Yr Price",
                field: "effective_first",
                // filter: "agSetColumnFilter",
                minWidth: 150,
                cellRenderer: (data) => {
                    // console.log('data', data.data);
                    // this.number();
                    if (data.data.type.includes("premium")) {
                        return (
                            "<div>" + data.data.effective_first_per + "%</div>"
                        );
                    } else {
                        return "<div>$" + data.data.effective_first + "</div>";
                    }
                },
            },
            {
                headerName: "Effective Multi-Yr Price",
                field: "effective_multi",
                // filter: "agSetColumnFilter",
                minWidth: 150,
                cellRenderer: (data) => {
                    // this.number();
                    if (data.data.type.includes("premium")) {
                        return (
                            "<div>" + data.data.effective_multi_per + "%</div>"
                        );
                    } else {
                        return "<div>$" + data.data.effective_multi + "</div>";
                    }
                },
            },
            {
                headerName: "Start Date",
                field: "start_date",
                filter: "agDateColumnFilter",
                minWidth: 175,
                filterParams: {
                    // Column-level filterParams replaces (not merges with)
                    // defaultColDef.filterParams, so these must be repeated
                    // here — without newRowsAction: "keep", ag-grid resets this
                    // filter's applied model on every rowData reload (i.e.
                    // every page change), dropping it from the next request.
                    // The backend (MisRepository) filters as a range match
                    // (start_date >= filters.start_date), so the client-side
                    // re-check ag-grid runs against the already server-filtered
                    // rows is locked to the matching "greaterThanOrEqual"
                    // operator instead of the "equals" default, which would
                    // otherwise wipe out rows the server correctly returned.
                    suppressAndOrCondition: true,
                    newRowsAction: "keep",
                    filterOptions: ["greaterThanOrEqual"],
                    comparator: function (
                        filterLocalDateAtMidnight,
                        cellValue
                    ) {
                        let dateAsString = cellValue;
                        if (dateAsString == null) return -1;
                        let dateParts = dateAsString.split("-");
                        let cellDate = new Date(
                            Number(dateParts[0]),
                            Number(dateParts[1]) - 1,
                            Number(dateParts[2])
                        );
                        if (
                            filterLocalDateAtMidnight.getTime() ===
                            cellDate.getTime()
                        ) {
                            return 0;
                        }
                        if (cellDate < filterLocalDateAtMidnight) {
                            return -1;
                        }
                        if (cellDate > filterLocalDateAtMidnight) {
                            return 1;
                        }
                    },
                    browserDatePicker: true,
                },
            },
            {
                headerName: "End Date",
                field: "end_date",
                filter: "agDateColumnFilter",
                minWidth: 175,
                filterParams: {
                    // See Start Date column: backend filters as end_date <=
                    // filters.end_date, so the client-side re-check is locked
                    // to "lessThanOrEqual" to match.
                    suppressAndOrCondition: true,
                    newRowsAction: "keep",
                    filterOptions: ["lessThanOrEqual"],
                    comparator: function (
                        filterLocalDateAtMidnight,
                        cellValue
                    ) {
                        let dateAsString = cellValue;
                        if (dateAsString == null) return -1;
                        let dateParts = dateAsString.split("-");
                        let cellDate = new Date(
                            Number(dateParts[0]),
                            Number(dateParts[1]) - 1,
                            Number(dateParts[2])
                        );
                        if (
                            filterLocalDateAtMidnight.getTime() ===
                            cellDate.getTime()
                        ) {
                            return 0;
                        }
                        if (cellDate < filterLocalDateAtMidnight) {
                            return -1;
                        }
                        if (cellDate > filterLocalDateAtMidnight) {
                            return 1;
                        }
                    },
                    browserDatePicker: true,
                },
            },
            {
                headerName: "Period (Yrs)",
                field: "period",
                filter: "agSetColumnFilter",
                filterParams: {
                    suppressAndOrCondition: true,
                    newRowsAction: "keep",
                    values: (params) => {
                        params.success(this.periodOptions);
                    },
                },
                minWidth: 120,
            },
            {
                headerName: "List Based",
                field: "list_based",
                filter: "agSetColumnFilter",
                filterParams: {
                    suppressAndOrCondition: true,
                    newRowsAction: "keep",
                    values: (params) => {
                        params.success(this.listBasedOptions.map((v) => (v === "yes" ? "Yes" : "No")));
                    },
                },
                // Yes/No display derived here rather than mutating the raw
                // "yes"/"no" row data — see FILTER_PARAM_MAP's valuesMap for
                // how the selected Yes/No gets translated back for the API.
                valueGetter: (data) => {
                    return data.data.list_based === "yes" ? "Yes" : "No";
                },
                cellRenderer: (data) => {
                    return data.data.list_based === "yes" ? "Yes" : "No";
                },
                minWidth: 120,
                // hide:true
            },
        ]),
            // Set Grid sidebar
            (this.sideBar = {
                toolPanels: [
                    {
                        id: "columns",
                        labelDefault: "Columns",
                        labelKey: "columns",
                        iconKey: "columns",
                        toolPanel: "agColumnsToolPanel",
                        toolPanelParams: {
                            suppressRowGroups: true,
                            suppressValues: true,
                            suppressPivots: true,
                            suppressPivotMode: true,
                            suppressSideButtons: true,
                            suppressColumnSelectAll: true,
                            suppressColumnExpandAll: true,
                        },
                    },
                ],
            });
    },
    mounted() {
        this.gridApi = this.gridOptions.api;

        //agigrid loader
        this.gridApi.showLoadingOverlay();

        // Filter dropdown options must resolve before restoring/creating any
        // Set Filter instance — ag-grid grabs a Set Filter's `values` list
        // synchronously on first use, so restoring a saved filter model before
        // resellerOptions/registrarOptions/tldOptions/amOptions load bakes in
        // an empty option list for that filter's lifetime.
        this.fetchFilterOptions().then(() => {
            // Restore search/filter/sort state persisted before this component
            // was last unmounted (e.g. switching away from this tab and back).
            this.restorePersistedState();

            // Table Data AXIOS Call
            this.populateTableData();
        });

        // Resize to fit columns
        this.sizeColumnsToFit();

        // Reset pinned columns on mobile
        this.toggleColumnPinned(this.windowWidth);

        this.$root.$on("tableRefresh", () => {
            // console.log('refresh');
            this.refreshPage();
        });
    },
    destroyed() {
        // Persist the current filter/sort/search state before unmount — the
        // debounce timer may not have fired yet if the user switched tabs
        // right after changing a filter, which would otherwise silently
        // drop that change instead of saving it.
        this.persistFilterState();

        // Clear Listing Count
        this.$store.dispatch("clearListingCount");

        clearTimeout(this.searchDebounceTimer);

        // Unbind Button Event Listeners
        this.$root.$off("tableRefresh");
    },
};
</script>

<style lang="scss">
.ag-selection-checkbox {
    display: flex;
    align-items: center;
    height: 36px;
}

.ag-row-disabled,
.ag-checkbox-disabled .ag-group-checkbox {
    pointer-events: none;
}

.ag-row-disabled .ag-group-checkbox,
.ag-checkbox-disabled .ag-group-checkbox {
    opacity: 0.3;
}

.ag-row-level-1 .ag-group-value,
.text-capitalize input {
    text-transform: capitalize;
}

//Hide grouping arrow
.ag-group-expanded,
.ag-group-contracted {
    display: none !important;
}

.ag-group-checkbox {
    margin-left: 0 !important;
}

.text-capitalize {
    text-transform: capitalize;
}
.ag-row-red {
    background: #fff1ef;
}
</style>
